import { create } from 'zustand';

import { normalizeError } from '../../api/core/errors';
import { getStudentEmailDomain } from '../../api/environment';
import { ExamSubmissionRepository } from '../../api/repository/exam-submission';
import { UnitRepository } from '../../api/repository/unit';
import { izteachSession } from '../../api/session';
import type { Id, IztExamQuestion } from '../../api/types';
import { useQltIztContentStore } from '../../stores/qlt';
import { userErrorMessage } from '../../stores/userError';
import { emailPattern } from '../submissions/submissions';
import { EXAM_CONCURRENCY, examCount, examLoadBlock, IZ_SIGNED_OUT, loadClassExamList, mapLimit, rosterRows, type ClassExamRef, type ExamCount, type ExamUnitRef } from './classExams';
import { toAttempt, type ExamAttempt } from './examResults';

export { IZ_SIGNED_OUT };

// per exam; the roster pattern narrows it server side, so only a class retaking one exam hundreds of times hits it
const ATTEMPT_LIMIT = 300;

/** The roster and the Kho bài tree query that names the class's IZ courses. */
export interface ExamLoadInput {
  codes: string[];
  classId: Id;
  gradeId?: Id;
  subjectId?: Id;
}

/** One exam's questions, for the per-question tallies and each student's sheet. */
export interface ExamQuestionsEntry {
  loading: boolean;
  error: string | null;
  questions: IztExamQuestion[];
}

/** One exam's roster attempts, read when its row or card opens. */
export interface ExamAttemptsEntry {
  loading: boolean;
  error: string | null;
  attempts: ExamAttempt[];
}

/** What one class's load holds; kept per load key for the session. */
export interface ClassExamSnapshot {
  /** Roster email regex; attempts are public, so a card reads them even signed out of IZ. */
  pattern: string;
  /** Until the units and every count have landed. */
  loading: boolean;
  error: string | null;
  exams: ClassExamRef[];
  counts: Record<string, ExamCount>;
  /** Counts this run has landed; a reload shows the old ones until each is replaced. */
  counted: number;
  attempts: Record<string, ExamAttemptsEntry>;
}

export interface ExamResultState extends ClassExamSnapshot {
  key: string | null;
  cache: Record<string, ClassExamSnapshot>;
  courseTitles: Record<string, string>;
  /** exam id to its unit's title, when a token could read it. */
  examTitles: Record<string, string>;
  /** exam id to its test unit, so a row opens its đề on the board. */
  examUnits: Record<string, ExamUnitRef>;
  load: (key: string, input: ExamLoadInput, force?: boolean) => Promise<void>;
  /** By exam id, for the current class; every open exam tab reads its own. */
  loadAttempts: (examId: string, force?: boolean) => Promise<void>;
  questions: Record<string, ExamQuestionsEntry>;
  loadQuestions: (examId: string, force?: boolean) => Promise<void>;
}

const EMPTY: ClassExamSnapshot = { pattern: '', loading: false, error: null, exams: [], counts: {}, counted: 0, attempts: {} };

// a forced reload of the same class bumps this, so the older run stops writing
const generation: Record<string, number> = {};

export const useExamResultStore = create<ExamResultState>((set, get) => {
  /** Writes a class's snapshot; it shows only while that class is current. */
  function put(key: string, patch: (snapshot: ClassExamSnapshot) => Partial<ClassExamSnapshot>) {
    set((state) => {
      const base = state.cache[key] || EMPTY;
      const change = patch(base);
      const cache = { ...state.cache, [key]: { ...base, ...change } };
      return state.key === key ? { ...change, cache } : { cache };
    });
  }

  return {
    key: null,
    ...EMPTY,
    cache: {},
    courseTitles: {},
    examTitles: {},
    examUnits: {},

    async load(key, input, force = false) {
      const cached = get().cache[key];
      if (!force && cached && !cached.error) {
        if (get().key !== key) set({ key, ...cached });
        return;
      }
      const run = (generation[key] || 0) + 1;
      generation[key] = run;
      const live = () => generation[key] === run;
      const domain = getStudentEmailDomain();
      const pattern = domain ? emailPattern(input.codes, domain) : '';
      const block = examLoadBlock(domain, pattern, Boolean(izteachSession.accessToken));
      // a reload keeps the rows until fresh counts replace them; Tải lại reads opened attempts again
      const prior = cached && cached.pattern === pattern ? cached : EMPTY;
      const keep = force ? {} : prior.attempts;
      set({ key, ...(cached || EMPTY) });
      if (block) { put(key, () => ({ ...EMPTY, pattern, error: block.error, attempts: keep })); return; }
      put(key, () => ({ pattern, loading: true, error: null, exams: prior.exams, counts: prior.counts, counted: 0, attempts: keep }));
      try {
        const tree = await useQltIztContentStore.getState().fetchTree({ class: input.classId, grade: input.gradeId, subject: input.subjectId });
        const list = await loadClassExamList(UnitRepository.titlesForCourse, tree);
        if (!live()) return;
        set({ examTitles: { ...get().examTitles, ...list.examTitles }, examUnits: { ...get().examUnits, ...list.examUnits }, courseTitles: { ...get().courseTitles, ...list.courseTitles } });
        put(key, () => ({ exams: list.exams }));
        // each row shows as its count lands
        await mapLimit(list.exams, EXAM_CONCURRENCY, async (exam) => {
          const count = await examCount((examId, states) => ExamSubmissionRepository.countByExam(examId, pattern, states), exam.examId);
          if (live()) put(key, (snapshot) => ({ counts: { ...snapshot.counts, [exam.examId]: count }, counted: snapshot.counted + 1 }));
        });
        if (live()) put(key, () => ({ loading: false }));
      } catch (error) {
        if (!live()) return;
        const kind = normalizeError(error).kind;
        put(key, () => ({ loading: false, error: kind === 'unauthorized' || kind === 'forbidden' ? IZ_SIGNED_OUT : userErrorMessage(error) }));
      }
    },

    async loadAttempts(examId, force = false) {
      const { key, pattern } = get();
      if (!key || !pattern) return;
      const current = get().attempts[examId];
      if (current && !force && (current.loading || !current.error)) return;
      const write = (entry: ExamAttemptsEntry) => put(key, (snapshot) => ({ attempts: { ...snapshot.attempts, [examId]: entry } }));
      write({ loading: true, error: null, attempts: current ? current.attempts : [] });
      try {
        const domain = getStudentEmailDomain();
        const rows = rosterRows(await ExamSubmissionRepository.listByExam(examId, pattern, ATTEMPT_LIMIT), pattern);
        const attempts: ExamAttempt[] = [];
        rows.forEach((row) => {
          const attempt = toAttempt(row, domain);
          if (attempt) attempts.push(attempt);
        });
        write({ loading: false, error: null, attempts });
      } catch (error) {
        write({ loading: false, error: userErrorMessage(error), attempts: [] });
      }
    },

    questions: {},

    async loadQuestions(examId, force = false) {
      const current = get().questions[examId];
      if (current && !force && (current.loading || !current.error)) return;
      const put = (entry: ExamQuestionsEntry) => set({ questions: { ...get().questions, [examId]: entry } });
      put({ loading: true, error: null, questions: current ? current.questions : [] });
      try {
        put({ loading: false, error: null, questions: await ExamSubmissionRepository.questionsOf(examId) });
      } catch (error) {
        put({ loading: false, error: userErrorMessage(error), questions: [] });
      }
    },
  };
});
